import { Capacitor } from "@capacitor/core";
import { CapacitorTwilioVoice } from "@capgo/capacitor-twilio-voice";
import { encodeVoiceIdentity } from "@vidimo/shared";
import type { ReservationStatus } from "@vidimo/shared/enums";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { interfaceTranslator } from "@/i18n/translate";
import { useApp } from "@/lib/context/use-powersync";
import type { ParsedCaller } from "./caller";
import { parseCaller } from "./caller";
import { transferCall as apiTransferCall } from "./client";
import {
  type CallSimulators,
  isSimulatedCall,
  useCallSimulators,
} from "./dev-simulators";
import { ensureMicGranted } from "./microphone";
import { useVoiceNativeListeners } from "./native-listeners";

type CallDirection = "incoming" | "outgoing";
export type CallStatus =
  | "ringing"
  | "connecting"
  | "connected"
  | "reconnecting";

/**
 * Synthetic context attached to dev-mode simulated calls. Lets the call UI
 * render the rich path (reservation pill) even when the live
 * `useCallerLookup` query misses — e.g. when seed data hasn't synced down
 * yet. Never set on real Twilio invites.
 */
export interface DevCallContext {
  reservationStatus?: ReservationStatus;
  unitCode?: string;
}

export interface ActiveCall {
  /**
   * An incoming call's caller, carried over from the invite it was answered
   * from. Absent on an outgoing call, and on an incoming one the app cannot
   * match to an invite (answered from the lock screen before the app started,
   * or on Android from the notification while another call is still ringing
   * or a call the host placed is still connecting).
   */
  caller?: ParsedCaller;
  callSid: string;
  dev?: DevCallContext;
  direction: CallDirection;
  displayName?: string;
  muted: boolean;
  /** Held in CallKit, as only iOS reports. */
  onHold: boolean;
  startedAt: number | null;
  status: CallStatus;
  to?: string;
}

/**
 * The call `makeCall` placed, until it is on record. Android emits no
 * `outgoingCallInitiated`, so its outgoing call is first known when it rings;
 * a ring while another call holds the slot gives it its sid instead.
 */
export interface PlacingCall extends Pick<ActiveCall, "displayName" | "to"> {
  callSid?: string;
  placedAt: number;
}

/** An invite still ringing: its caller, and when it arrived. */
export interface RingingInvite {
  /** Accepted in the app: on Android its call gets a sid of its own. */
  accepted?: boolean;
  caller: ParsedCaller;
  receivedAt: number;
}

export interface PendingInvite {
  /**
   * The caller, parsed once at invite time from the raw Twilio fields. The
   * IncomingCall UI branches on `caller.kind` (external guest vs staff) and
   * reads already-resolved attributes (`e164`, `transport`, `displayNumber`)
   * instead of re-interpreting `from` / custom params. See {@link parseCaller}.
   */
  caller: ParsedCaller;
  callSid: string;
  dev?: DevCallContext;
}

interface VoiceContextValue extends CallSimulators {
  /** Answers the invite, which on iOS holds the call on screen. */
  acceptInvite: () => Promise<void>;
  activeCall: ActiveCall | null;
  /** Hangs up the call on screen, then answers the invite. */
  endAndAcceptInvite: () => Promise<void>;
  /** Hangs up `callSid`, the call a screen showed, while it is on record. */
  endCall: (callSid: string) => Promise<void>;
  /** A call that ended while another stayed on record, for a few seconds. */
  endedCall: ActiveCall | null;
  /** The newest call on hold behind the one on screen. */
  heldCall: ActiveCall | null;
  /** Whether an answer holds the live call: only iOS's plugin holds one. */
  holdSupported: boolean;
  makeCall: (args: { to: string; displayName?: string }) => Promise<void>;
  pendingInvite: PendingInvite | null;
  /** True when native plugin is registered with Twilio (ready for incoming calls). */
  registered: boolean;
  rejectInvite: () => Promise<void>;
  /** Takes the call on screen off hold. */
  resumeCall: () => Promise<void>;
  /**
   * Whether audio is routed to the loudspeaker. Held here, beside `muted`,
   * because the in-call bar and the full-screen call page are two views of the
   * same call and each can toggle it; it resets when the call ends so the next
   * call starts on the earpiece.
   */
  speakerOn: boolean;
  /** True when running on a native platform (Capacitor iOS/Android). Web is no-op. */
  supported: boolean;
  /** Holds the call on screen and resumes `heldCall` in its place. */
  swapCalls: () => Promise<void>;
  toggleMute: () => Promise<void>;
  /** Flips `speakerOn` optimistically and reverts it if the audio route fails. */
  toggleSpeaker: () => Promise<void>;
  /**
   * Blind-transfer the active call to another staff member (by membership
   * id) of the property it rang for. The caller's leg is dropped server-side;
   * the resulting `callDisconnected` clears the local call state.
   */
  transferCall: (targetMemberId: string) => Promise<void>;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

const ENDED_CALL_NOTICE_MS = 4000;

export function VoiceProvider({ children }: { children: React.ReactNode }) {
  const supported = Capacitor.isNativePlatform();
  const history = useHistory();
  // The Twilio identity we mint server-side is `<memberId>__<propertyId>`,
  // so a property switch needs a fresh token + login. Subscribe to the
  // active property so the register effect re-runs on change.
  const { currentProperty } = useApp();
  const activePropertyId = currentProperty?.id ?? null;
  const [registered, setRegistered] = useState(false);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [pendingInvite, setPendingInvite] = useState<PendingInvite | null>(
    null
  );
  const activeCallRef = useRef<ActiveCall | null>(null);
  // Every write of the call goes through here, ahead of React's render, so
  // native events and taps that land in one frame each see the one before.
  const recordCall = useCallback((next: ActiveCall | null) => {
    activeCallRef.current = next;
    setActiveCall(next);
  }, []);
  // Live calls a newer one took the screen from, newest last, written as the
  // call on screen is.
  const [heldCalls, setHeldCalls] = useState<ActiveCall[]>([]);
  const heldCallsRef = useRef<ActiveCall[]>([]);
  const recordHeldCalls = useCallback((next: ActiveCall[]) => {
    heldCallsRef.current = next;
    setHeldCalls(next);
  }, []);
  const [endedCall, setEndedCall] = useState<ActiveCall | null>(null);
  const recordEndedCall = useCallback((ended: ActiveCall) => {
    setEndedCall(ended);
    window.setTimeout(() => {
      setEndedCall((shown) => (shown === ended ? null : shown));
    }, ENDED_CALL_NOTICE_MS);
  }, []);

  /**
   * Drops an ended call from the screen or from behind it, saying whether
   * either held it. With `handBack`, the newest call behind takes the screen.
   */
  const dropCall = useCallback(
    (callSid: string, { handBack }: { handBack: boolean }): boolean => {
      const shown = activeCallRef.current;
      const behind = heldCallsRef.current.filter(
        (call) => call.callSid !== callSid
      );
      const ended =
        shown?.callSid === callSid
          ? shown
          : heldCallsRef.current.find((call) => call.callSid === callSid);
      if (!ended) {
        return false;
      }
      if (ended === shown) {
        const handedBack = handBack ? behind.pop() : undefined;
        recordCall(handedBack ?? null);
      }
      recordHeldCalls(behind);
      if (activeCallRef.current) {
        recordEndedCall(ended);
      }
      return true;
    },
    [recordCall, recordEndedCall, recordHeldCalls]
  );

  /**
   * Records a call going on or off hold. A call coming off hold is the one
   * the host talks on, so it takes the screen, whether the app or CallKit's
   * own screen resumed it.
   */
  const recordHold = useCallback(
    (callSid: string, onHold: boolean) => {
      const shown = activeCallRef.current;
      if (shown?.callSid === callSid) {
        recordCall({ ...shown, onHold });
        return;
      }
      const held = heldCallsRef.current.find(
        (call) => call.callSid === callSid
      );
      if (!held) {
        return;
      }
      const behind = heldCallsRef.current.filter((call) => call !== held);
      if (onHold) {
        recordHeldCalls([...behind, { ...held, onHold }]);
        return;
      }
      recordHeldCalls(shown ? [...behind, shown] : behind);
      recordCall({ ...held, onHold });
    },
    [recordCall, recordHeldCalls]
  );
  // Every invite still ringing, by its id: where a connecting call finds the
  // caller it was answered from.
  const ringingInvitesRef = useRef(new Map<string, RingingInvite>());
  const placingCallRef = useRef<PlacingCall | null>(null);
  const pendingInviteRef = useRef<PendingInvite | null>(null);
  // Like `recordCall`: native events that land in one frame each see the
  // invite the one before left on screen.
  const recordInvite = useCallback((next: PendingInvite | null) => {
    pendingInviteRef.current = next;
    setPendingInvite(next);
  }, []);
  const historyRef = useRef(history);
  historyRef.current = history;

  useVoiceNativeListeners({
    activeCallRef,
    activePropertyId,
    dropCall,
    heldCallsRef,
    historyRef,
    pendingInviteRef,
    placingCallRef,
    recordCall,
    recordHeldCalls,
    recordHold,
    recordInvite,
    ringingInvitesRef,
    setRegistered,
    supported,
  });

  const makeCall = useCallback<VoiceContextValue["makeCall"]>(
    async ({ to, displayName }) => {
      if (!supported) {
        toast.error(
          interfaceTranslator("communications.voiceContext")("errorUnsupported")
        );
        return;
      }
      if (!(await ensureMicGranted())) {
        return;
      }
      placingCallRef.current = { displayName, placedAt: Date.now(), to };
      try {
        await CapacitorTwilioVoice.makeCall({ displayName, to });
      } catch (err) {
        placingCallRef.current = null;
        throw err;
      }
    },
    [supported]
  );

  // Both read the invite through the ref and re-check it after each await: it
  // can be cancelled, or a newer one ring in, meanwhile.
  const acceptInvite = useCallback(async () => {
    const invite = pendingInviteRef.current;
    if (!invite) {
      return;
    }
    if (isSimulatedCall(invite.callSid)) {
      recordInvite(null);
      const shown = activeCallRef.current;
      if (shown && !isSimulatedCall(shown.callSid)) {
        return;
      }
      if (shown) {
        recordHeldCalls([...heldCallsRef.current, { ...shown, onHold: true }]);
      }
      recordCall({
        caller: invite.caller,
        callSid: invite.callSid,
        dev: invite.dev,
        direction: "incoming",
        muted: false,
        onHold: false,
        startedAt: Date.now(),
        status: "connected",
      });
      return;
    }
    const ringing = ringingInvitesRef.current;
    const markAccepted = (accepted: boolean) => {
      const entry = ringing.get(invite.callSid);
      if (entry) {
        ringing.set(invite.callSid, { ...entry, accepted });
      }
    };
    // Marked before the microphone check, so a second tap never reaches the
    // plugin: Android would accept the invite again.
    if (ringing.get(invite.callSid)?.accepted) {
      return;
    }
    markAccepted(true);
    if (!((await ensureMicGranted()) && ringing.has(invite.callSid))) {
      markAccepted(false);
      return;
    }
    try {
      await CapacitorTwilioVoice.acceptCall({ callSid: invite.callSid });
    } catch (err) {
      markAccepted(false);
      throw err;
    }
  }, [recordCall, recordHeldCalls, recordInvite]);

  const rejectInvite = useCallback(async () => {
    const invite = pendingInviteRef.current;
    if (!invite) {
      return;
    }
    if (isSimulatedCall(invite.callSid)) {
      recordInvite(null);
      return;
    }
    ringingInvitesRef.current.delete(invite.callSid);
    await CapacitorTwilioVoice.rejectCall({ callSid: invite.callSid });
    if (pendingInviteRef.current?.callSid === invite.callSid) {
      recordInvite(null);
    }
  }, [recordInvite]);

  // By the sid the tap saw: the plugin's newest call may be another by now.
  const endCall = useCallback(
    async (callSid: string) => {
      const onRecord =
        activeCallRef.current?.callSid === callSid ||
        heldCallsRef.current.some((call) => call.callSid === callSid);
      if (!onRecord) {
        return;
      }
      if (isSimulatedCall(callSid)) {
        dropCall(callSid, { handBack: true });
        return;
      }
      try {
        await CapacitorTwilioVoice.endCall({ callSid });
      } catch (err) {
        // iOS refuses only a call that ended under the tap: it is over.
        if (Capacitor.getPlatform() === "ios") {
          console.error("[voice] hang-up of an ended call", err);
          return;
        }
        toast.error(
          interfaceTranslator("communications.voiceContext")("errorNotHungUp")
        );
        throw err;
      }
    },
    [dropCall]
  );

  // The invite End & Accept is answering, so a second tap never reaches the
  // plugin: with the live call gone, its hang-up would end the new one.
  const endingForInviteRef = useRef<string | null>(null);

  // The live call is the one on screen at the tap, ended by its own sid, and
  // only once the microphone is granted and the invite still rings: hanging up
  // the live guest for an answer that never comes would drop both.
  const endAndAcceptInvite = useCallback(async () => {
    const invite = pendingInviteRef.current;
    const live = activeCallRef.current;
    if (
      !(invite && live) ||
      endingForInviteRef.current === invite.callSid ||
      ringingInvitesRef.current.get(invite.callSid)?.accepted
    ) {
      return;
    }
    // A simulated invite holds no real call, as `acceptInvite` refuses too.
    if (isSimulatedCall(invite.callSid) && !isSimulatedCall(live.callSid)) {
      return;
    }
    endingForInviteRef.current = invite.callSid;
    try {
      if (!(isSimulatedCall(invite.callSid) || (await ensureMicGranted()))) {
        return;
      }
      if (
        pendingInviteRef.current?.callSid !== invite.callSid ||
        activeCallRef.current?.callSid !== live.callSid
      ) {
        return;
      }
      await endCall(live.callSid);
      try {
        await acceptInvite();
      } catch (err) {
        console.error("[voice] answer after hang-up failed", err);
        toast.error(
          interfaceTranslator("communications.nativeListeners")(
            "errorCallFailed"
          )
        );
      }
    } finally {
      endingForInviteRef.current = null;
    }
  }, [acceptInvite, endCall]);

  /**
   * Holds or resumes a call, saying whether it went through; the plugin's
   * `callHoldChanged` records it.
   */
  const setOnHold = useCallback(
    async (
      { callSid }: ActiveCall,
      onHold: boolean,
      failure: "errorNotResumed" | "errorNotSwitched"
    ): Promise<boolean> => {
      if (isSimulatedCall(callSid)) {
        recordHold(callSid, onHold);
        return true;
      }
      try {
        await CapacitorTwilioVoice.setCallOnHold({ callSid, onHold });
        return true;
      } catch (err) {
        console.error("[voice] hold change failed", err);
        toast.error(
          interfaceTranslator("communications.voiceContext")(failure)
        );
        return false;
      }
    },
    [recordHold]
  );

  const resumeCall = useCallback(async () => {
    const { current } = activeCallRef;
    if (current?.onHold) {
      await setOnHold(current, false, "errorNotResumed");
    }
  }, [setOnHold]);

  /** The held call, once resumed, takes the screen as `recordHold` hands it. */
  const swapCalls = useCallback(async () => {
    const shown = activeCallRef.current;
    const held = heldCallsRef.current.at(-1);
    if (!(shown && held)) {
      return;
    }
    if (!(shown.onHold || (await setOnHold(shown, true, "errorNotSwitched")))) {
      return;
    }
    await setOnHold(held, false, "errorNotSwitched");
  }, [setOnHold]);

  const toggleMute = useCallback(async () => {
    const { current } = activeCallRef;
    if (!current) {
      return;
    }
    const nextMuted = !current.muted;
    if (!isSimulatedCall(current.callSid)) {
      await CapacitorTwilioVoice.muteCall({
        callSid: current.callSid,
        muted: nextMuted,
      });
    }
    const latest = activeCallRef.current;
    if (latest?.callSid === current.callSid) {
      recordCall({ ...latest, muted: nextMuted });
    }
  }, [recordCall]);

  const [speakerOn, setSpeakerOn] = useState(false);
  useEffect(() => {
    if (!activeCall) {
      setSpeakerOn(false);
    }
  }, [activeCall]);

  const toggleSpeaker = useCallback(async () => {
    const { current } = activeCallRef;
    if (!current) {
      return;
    }
    const next = !speakerOn;
    setSpeakerOn(next);
    if (isSimulatedCall(current.callSid)) {
      return;
    }
    try {
      await CapacitorTwilioVoice.setSpeaker({ enabled: next });
    } catch (err) {
      console.error("[voice] speaker route failed", err);
      setSpeakerOn(!next);
    }
  }, [speakerOn]);

  const transferCall = useCallback<VoiceContextValue["transferCall"]>(
    async (targetMemberId) => {
      const t = interfaceTranslator("communications.voiceContext");
      const { current } = activeCallRef;
      if (!current) {
        toast.error(t("errorNoActiveCall"));
        return;
      }
      if (isSimulatedCall(current.callSid)) {
        toast.success("Call transferred (simulated)");
        dropCall(current.callSid, { handBack: true });
        return;
      }
      try {
        await apiTransferCall({
          callSid: current.callSid,
          propertyId: current.caller?.propertyId ?? null,
          targetMemberId,
        });
        // The caller's leg is dropped server-side; the plugin's
        // callDisconnected event clears the UI. Nothing else to do here.
      } catch (err) {
        console.error("[voice] transfer failed", err);
        toast.error(t("errorNotTransferred"));
      }
    },
    [dropCall]
  );

  // On the web the only calls are the simulator's, which hold in the app.
  const holdSupported = !supported || Capacitor.getPlatform() === "ios";
  // Where an answer holds the live call, every call behind the screen is held,
  // CallKit's report of it or not; on Android one behind is still live.
  const heldCall = holdSupported ? (heldCalls.at(-1) ?? null) : null;

  const simulators = useCallSimulators({
    activePropertyId,
    dropCall,
    historyRef,
    recordCall,
    recordInvite,
  });

  const value = useMemo<VoiceContextValue>(
    () => ({
      acceptInvite,
      activeCall,
      endAndAcceptInvite,
      endCall,
      endedCall,
      heldCall,
      holdSupported,
      makeCall,
      pendingInvite,
      registered,
      rejectInvite,
      resumeCall,
      speakerOn,
      supported,
      swapCalls,
      toggleMute,
      toggleSpeaker,
      transferCall,
      ...simulators,
    }),
    [
      registered,
      supported,
      activeCall,
      pendingInvite,
      makeCall,
      acceptInvite,
      rejectInvite,
      endCall,
      endAndAcceptInvite,
      endedCall,
      heldCall,
      holdSupported,
      resumeCall,
      swapCalls,
      toggleMute,
      speakerOn,
      toggleSpeaker,
      transferCall,
      simulators,
    ]
  );

  return (
    <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>
  );
}

export function useVoice(): VoiceContextValue {
  const ctx = useContext(VoiceContext);
  if (!ctx) {
    throw new Error("useVoice must be used within VoiceProvider");
  }
  return ctx;
}

/**
 * Build the `{ to, displayName }` payload that `voice.makeCall` expects
 * for a staff-to-staff call. The recipient's identity is scoped to the
 * **caller's** propertyId so the TwiML webhook rings the device session
 * registered under that property, and no other.
 *
 * Keeping this as a top-level helper (rather than a method on the voice
 * context) keeps `voice.makeCall` single-purpose: it doesn't need to
 * know about staff vs guest dispatch.
 */
export function buildStaffCallTarget(
  recipientMemberId: string,
  callerPropertyId: string,
  displayName?: string
): { to: string; displayName?: string } {
  return {
    displayName,
    to: `client:${encodeVoiceIdentity(recipientMemberId, callerPropertyId)}`,
  };
}
